'use client'

import React, { useMemo } from 'react'
import { useLocale, useTranslations } from 'next-intl'

import { HOLIDAY_THEME_LABEL_KEY, type HolidayTheme } from '@/src/constants/holidays'
import type { HolidayPackageFacet, HolidayPackageFilters } from '@/src/utils/holidays/packageFilters'
import { formatHolidayMonth, hasActiveHolidayPackageFilters } from '@/src/utils/holidays/packageFilters'

import PackagesFilterPanel, { type FilterOption } from './packagesFilterPanel'

interface PackagesFilterPanelWrapperProps {
    cityFacets: HolidayPackageFacet[]
    monthFacets: HolidayPackageFacet[]
    occasionFacets: HolidayPackageFacet[]
    themeFacets: HolidayPackageFacet[]
    filters: HolidayPackageFilters
    onToggleCity: (key: string) => void
    onToggleMonth: (key: string) => void
    onToggleOccasion: (key: string) => void
    onToggleTravelStyle: (key: string) => void
    onClearAll: () => void
    isMobile: boolean
}

const PackagesFilterPanelWrapper: React.FC<PackagesFilterPanelWrapperProps> = ({
    cityFacets,
    monthFacets,
    occasionFacets,
    themeFacets,
    filters,
    onToggleCity,
    onToggleMonth,
    onToggleOccasion,
    onToggleTravelStyle,
    onClearAll,
    isMobile
}) => {
    const t = useTranslations('holidaysPage')
    const locale = useLocale()

    const cityOptions: FilterOption[] = useMemo(
        () => cityFacets.map(facet => ({ ...facet, selected: filters.cities.includes(facet.key) })),
        [cityFacets, filters.cities]
    )

    const monthOptions: FilterOption[] = useMemo(
        () =>
            monthFacets.map(facet => ({
                ...facet,
                label: formatHolidayMonth(facet.key, locale),
                selected: filters.months.includes(facet.key)
            })),
        [monthFacets, filters.months, locale]
    )

    const occasionOptions: FilterOption[] = useMemo(
        () => occasionFacets.map(facet => ({ ...facet, selected: filters.occasions.includes(facet.key) })),
        [occasionFacets, filters.occasions]
    )

    const travelStyleOptions: FilterOption[] = useMemo(
        () =>
            themeFacets.map(facet => ({
                ...facet,
                label: t(HOLIDAY_THEME_LABEL_KEY[facet.key as HolidayTheme]),
                selected: filters.themes.includes(facet.key)
            })),
        [themeFacets, filters.themes, t]
    )

    return (
        <PackagesFilterPanel
            title={t('filtersTitle')}
            clearAllLabel={t('filtersClearAll')}
            onClearAll={onClearAll}
            hasActiveFilters={hasActiveHolidayPackageFilters(filters)}
            destinationTitle={t('filterDestination')}
            destinationSearchPlaceholder={t('filterDestinationSearch')}
            cityOptions={cityOptions}
            onToggleCity={onToggleCity}
            showAllLabel={t('filterShowAllDestinations', { count: cityFacets.length })}
            showLessLabel={t('filterShowLess')}
            showMoreLabel={t('filterShowMore')}
            monthTitle={t('filterDepartureMonth')}
            monthOptions={monthOptions}
            onToggleMonth={onToggleMonth}
            occasionTitle={t('filterOccasion')}
            occasionOptions={occasionOptions}
            onToggleOccasion={onToggleOccasion}
            travelStyleTitle={t('filterTravelStyle')}
            travelStyleOptions={travelStyleOptions}
            onToggleTravelStyle={onToggleTravelStyle}
            noMatchesLabel={t('filterNoDestinations')}
            clearSearchLabel={t('clearSearch')}
            isMobile={isMobile}
        />
    )
}

export { PackagesFilterPanelWrapper }
export default PackagesFilterPanelWrapper
